import { useCallback, useEffect, useState } from 'react';
import { Alert, Card, Description, Form, Input, Label, TextField } from '@heroui/react';
import { AttributeField } from '../components/AttributeField.tsx';
import { ActionButton } from '../components/ActionButton.tsx';
import { PageLoader } from '../components/PageLoader.tsx';
import { useAction } from '../lib/useAction.ts';
import { errorText } from '../lib/errors.ts';
import { useAuth } from '../auth/AuthProvider.tsx';
import {
  fetchCatalogue,
  fetchMyCharacter,
  saveMyAttributes,
  saveMyCharacterName,
  type AttributeDef,
  type Character,
} from '../data/api.ts';

/**
 * A member's own character (DECISION-5, DECISION-7).
 *
 * Members may edit their character's name and its CHARACTER attributes.
 * Time-series data is admin-entered and shown read-only elsewhere.
 */
const ROLE_LABELS: Record<string, string> = {
  admin: 'администратор',
  officer: 'офицер',
  member: 'участник',
};

export function MyCharacterPage() {
  const { user, role } = useAuth();
  const [character, setCharacter] = useState<Character | null>(null);
  const [defs, setDefs] = useState<AttributeDef[]>([]);
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [name, setName] = useState('');
  const [status, setStatus] = useState<'loading' | 'ready'>('loading');
  /**
   * The LOAD's failure, and only the load's.
   *
   * ⚠️ NOT SHARED WITH THE SAVE ANY MORE. One `error` state served both, so a
   * save that failed left a banner sitting above the form until the next
   * attempt cleared it, and a failed LOAD was cleared by pressing Сохранить —
   * which said the page had recovered when nothing had been re-fetched. A load
   * failure is a fact about what is on screen and stays on screen; a save
   * failure is news and goes to a toast.
   */
  const [loadError, setLoadError] = useState<string | null>(null);
  const action = useAction();

  const load = useCallback(async () => {
    try {
      const [catalogue, mine] = await Promise.all([fetchCatalogue(), fetchMyCharacter()]);
      setDefs(catalogue.character);
      setCharacter(mine.character);
      setName(mine.character?.name ?? '');

      const byKey: Record<string, unknown> = {};
      for (const def of catalogue.character) {
        byKey[def.key] = mine.values.find((v) => v.defId === def.id)?.value ?? null;
      }
      setValues(byKey);
      setStatus('ready');
    } catch (caught) {
      setLoadError(errorText(caught, 'Не удалось загрузить вашего персонажа.'));
      setStatus('ready');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    void action.run(
      'save',
      async () => {
        if (name.trim() !== (character?.name ?? '')) {
          const result = await saveMyCharacterName(name.trim());
          setCharacter(result.character);
        }
        /*
          ⚠️ ADMIN-ONLY FIELDS ARE STRIPPED FROM THE PAYLOAD, not merely disabled
          on screen. The server refuses the WHOLE save if one is present — which is
          right, because silently dropping a field would report success for an edit
          that did not happen — so sending back a value the form only ever
          displayed would make every save fail for anyone whose catalogue has one.
        */
        const writable = Object.fromEntries(
          Object.entries(values).filter(
            ([key]) => !defs.some((def) => def.key === key && def.adminOnly),
          ),
        );
        if (Object.keys(writable).length > 0) await saveMyAttributes(writable);
      },
      { success: 'Сохранено.', error: 'Не удалось сохранить.' },
    );
  }

  if (status === 'loading') return <PageLoader />;

  return (
    <div className="fade-in mx-auto flex w-full max-w-3xl flex-col gap-4">
      {/* At the TOP, not above the button: it explains why the fields below are
          empty, which is a statement about the whole page. */}
      {loadError && <Alert status="danger">{loadError}</Alert>}

      {/*
        ⚠️ MOVED HERE FROM THE HOME PAGE (2026-08-12, owner's call). It was the
        first card on the landing page, above the two lists everybody comes for,
        and it told you three things you already knew about yourself.

        It carries TWO of the old three rows. «Персонаж» is gone deliberately —
        the character's name is the editable field twelve pixels below it, and a
        read-only copy of a value you are looking at in a text box is not
        information, it is a second thing to keep in step.

        OUTSIDE the <Form>: nothing here is editable, and a definition list
        inside a form invites the reading that it is.
      */}
      <Card>
        <Card.Header>
          <Card.Title>О вас</Card.Title>
        </Card.Header>
        <Card.Content className="flex flex-col gap-3">
          {/* A definition list rather than a table: two columns that stack on a
              phone without losing the label→value pairing. */}
          <dl className="grid grid-cols-[minmax(6rem,max-content)_1fr] gap-x-4 gap-y-2 text-sm">
            <dt className="text-muted">Telegram</dt>
            <dd className="min-w-0 truncate">
              {user?.username ? `@${user.username}` : (user?.telegramUserId ?? '—')}
            </dd>
            <dt className="text-muted">Роль</dt>
            <dd>{role ? (ROLE_LABELS[role] ?? role) : '—'}</dd>
          </dl>
          <p className="text-sm text-muted">
            Роль берётся из вашего статуса в Telegram-группе и проверяется при каждом запросе —
            выход из группы закрывает доступ немедленно.
          </p>
        </Card.Content>
      </Card>

      {/* HeroUI's Form is RAC's, so this is a real <form>: Enter submits, and
          validationBehavior="native" keeps the browser's own required-field
          check — the same guard the plain <input required> gave before. */}
      <Form onSubmit={submit} validationBehavior="native" className="flex flex-col gap-4">
        <Card>
          <Card.Header>
            <Card.Title>Персонаж</Card.Title>
          </Card.Header>
          <Card.Content>
            {/* TextField is the RAC field wrapper; the actual control is the
                nested Input, which is where native attributes belong. The
                64-character limit mirrors the server's, which answers
                'Character name is too long (64 character limit).' */}
            <TextField value={name} onChange={setName} isRequired fullWidth>
              <Label>Имя</Label>
              <Input maxLength={64} />
              {/* Create vs edit: the only cue that saving will CREATE rather
                  than rename. As a Description it is announced with the field
                  rather than floating beside it. */}
              {!character && (
                <Description>
                  У вас пока нет персонажа — введите имя и сохраните, чтобы создать его.
                </Description>
              )}
            </TextField>
          </Card.Content>
        </Card>

        {defs.length > 0 && (
          <Card>
            <Card.Header>
              <Card.Title>Атрибуты</Card.Title>
            </Card.Header>
            <Card.Content className="flex flex-col gap-4">
              {defs.map((def) => (
                <AttributeField
                  key={def.key}
                  def={{
                    ...def,
                    // Says WHY it is greyed out. A disabled field with no
                    // explanation reads as a bug in the page.
                    description: def.adminOnly
                      ? [def.description, 'Заполняет администратор.'].filter(Boolean).join(' ')
                      : def.description,
                  }}
                  value={values[def.key]}
                  /* Attribute values hang off a character row that does not
                     exist yet, so they stay locked until the first save
                     creates one — the server answers 'Create your character
                     first.' otherwise. (Was `!character && def.key !==
                     undefined && character === null`: the same test written
                     three times, `def.key` being a string that is never
                     undefined.) */
                  disabled={!character || def.adminOnly}
                  onChange={(value) => setValues((prev) => ({ ...prev, [def.key]: value }))}
                />
              ))}
            </Card.Content>
          </Card>
        )}

        {defs.length === 0 && (
          <Card>
            <Card.Content className="flex flex-col gap-1">
              <p className="text-sm text-muted">Атрибуты персонажа ещё не настроены.</p>
              <p className="text-xs text-muted">
                Их добавляет администратор в разделе Админка → Атрибуты.
              </p>
            </Card.Content>
          </Card>
        )}

        {/*
          ⚠️ THE «Сохранено.» AND «Не удалось сохранить.» BANNERS THAT STOOD
          HERE ARE TOASTS NOW. Both were outcomes of a press, and both had the
          same defect: they appeared BELOW the fields and ABOVE the button, so
          on a phone the confirmation of a save landed off screen under the
          keyboard, and the green one stayed there until the next submit —
          telling a reader who had since edited three fields that their work was
          saved.
        */}

        {/* Full width on a phone, where it is the only thing in its row and a
            thumb should not have to aim; natural width from md up. */}
        <ActionButton
          type="submit"
          className="w-full md:w-auto md:self-start"
          isPending={action.isPending('save')}
          isDisabled={action.isBusy || name.trim() === ''}
        >
          Сохранить
        </ActionButton>
      </Form>
    </div>
  );
}
